(() => {
  "use strict";

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const routesRoot = window.Shopify?.routes?.root || "/";
  const endpoint = (path) => `${routesRoot}${path}`.replace(/\/{2,}/g, "/");
  const state = { menuOpen: false, cartOpen: false, lastFocus: null };

  const setScrollLock = () => {
    document.body.classList.toggle("lewa-scroll-locked", state.menuOpen || state.cartOpen);
  };

  const setExpanded = (elements, expanded) => {
    elements.forEach((element) => element.setAttribute("aria-expanded", String(expanded)));
  };

  const focusFirst = (container) => {
    const target = $(
      "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      container,
    );
    target?.focus();
  };

  const initHeader = () => {
    const header = $("[data-lewa-header], .lewa-header");
    if (!header) return;
    const update = () => header.classList.toggle("lewa-is-scrolled", window.scrollY > 24);
    update();
    window.addEventListener("scroll", update, { passive: true });
  };

  const initAnnouncement = () => {
    const root = $("[data-lewa-announcement], .lewa-announcement");
    if (!root) return;
    const messages = $$("[data-lewa-announcement-message], .lewa-announcement__message", root);
    if (messages.length < 2) {
      messages[0]?.classList.add("lewa-is-active");
      return;
    }
    let index = Math.max(0, messages.findIndex((message) => message.classList.contains("lewa-is-active")));
    const show = (next) => {
      index = (next + messages.length) % messages.length;
      messages.forEach((message, messageIndex) => {
        const active = messageIndex === index;
        message.classList.toggle("lewa-is-active", active);
        message.setAttribute("aria-hidden", String(!active));
      });
    };
    show(index);
    const interval = Number(root.dataset.interval) || 4500;
    if (!reducedMotion.matches) window.setInterval(() => show(index + 1), interval);
  };

  const initMobileMenu = () => {
    const menu = $("[data-lewa-mobile-menu], .lewa-mobile-menu");
    if (!menu) return;
    const openers = $$("[data-lewa-menu-open]");
    const closers = $$("[data-lewa-menu-close]", menu);
    const close = ({ restoreFocus = true } = {}) => {
      state.menuOpen = false;
      menu.classList.remove("lewa-is-open");
      menu.setAttribute("aria-hidden", "true");
      setExpanded(openers, false);
      setScrollLock();
      if (restoreFocus) state.lastFocus?.focus?.();
    };
    const open = () => {
      document.dispatchEvent(new CustomEvent("lewa:close-overlays"));
      state.lastFocus = document.activeElement;
      state.menuOpen = true;
      menu.classList.add("lewa-is-open");
      menu.setAttribute("aria-hidden", "false");
      setExpanded(openers, true);
      setScrollLock();
      focusFirst(menu);
    };
    openers.forEach((button) => button.addEventListener("click", open));
    closers.forEach((button) => button.addEventListener("click", () => close()));
    $$("a[href]", menu).forEach((link) => link.addEventListener("click", () => close({ restoreFocus: false })));
    menu.addEventListener("click", (event) => {
      if (event.target.matches("[data-lewa-menu-backdrop]")) close();
    });
    document.addEventListener("lewa:close-overlays", () => close({ restoreFocus: false }));
  };

  const initCarousel = () => {
    $$("[data-lewa-carousel], [data-lewa-works], .lewa-carousel").forEach((carousel) => {
      const cards = $$("[data-lewa-carousel-card], [data-lewa-works-card], .lewa-carousel__card", carousel);
      const dots = $$("[data-lewa-carousel-dot], [data-lewa-works-dot], .lewa-carousel__dot", carousel);
      const previous = $("[data-lewa-carousel-prev], [data-lewa-works-prev]", carousel);
      const next = $("[data-lewa-carousel-next], [data-lewa-works-next]", carousel);
      if (!cards.length) return;
      let index = Math.max(0, cards.findIndex((card) => card.getAttribute("aria-current") === "true"));
      let paused = false;
      let timer;

      const render = () => {
        const step = window.innerWidth >= 768 ? 230 : 200;
        cards.forEach((card, cardIndex) => {
          const distance = cardIndex - index;
          const absolute = Math.abs(distance);
          card.style.setProperty("--lewa-x", `${distance * step}px`);
          card.style.setProperty("--lewa-y", `${absolute * absolute * 14}px`);
          card.style.setProperty("--lewa-tilt", `${Math.max(-14, Math.min(14, distance * 7))}deg`);
          card.style.setProperty("--lewa-scale", String(Math.max(.62, 1 - absolute * .12)));
          card.style.setProperty("--lewa-opacity", String(Math.max(.35, 1 - absolute * .18)));
          card.style.setProperty("--lewa-blur", `${absolute >= 3 ? 2 : absolute >= 2 ? 1 : 0}px`);
          card.style.setProperty("--lewa-z", String(100 - absolute));
          card.setAttribute("aria-current", String(cardIndex === index));
        });
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle("lewa-is-active", dotIndex === index);
          dot.classList.toggle("is-active", dotIndex === index);
          dot.setAttribute("aria-current", String(dotIndex === index));
        });
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === cards.length - 1;
      };
      const go = (nextIndex, wrap = false) => {
        index = wrap
          ? (nextIndex + cards.length) % cards.length
          : Math.max(0, Math.min(cards.length - 1, nextIndex));
        render();
      };
      const start = () => {
        window.clearInterval(timer);
        if (!paused && cards.length > 1 && !reducedMotion.matches && carousel.dataset.lewaAutoplay !== "false") {
          timer = window.setInterval(() => go(index + 1, true), Number(carousel.dataset.lewaAutoplayDelay) || 3200);
        }
      };
      cards.forEach((card, cardIndex) => card.addEventListener("click", () => go(cardIndex)));
      dots.forEach((dot, dotIndex) => dot.addEventListener("click", () => go(dotIndex)));
      previous?.addEventListener("click", () => go(index - 1));
      next?.addEventListener("click", () => go(index + 1));
      carousel.addEventListener("mouseenter", () => { paused = true; start(); });
      carousel.addEventListener("mouseleave", () => { paused = false; start(); });
      carousel.addEventListener("focusin", () => { paused = true; start(); });
      carousel.addEventListener("focusout", () => { paused = false; start(); });
      carousel.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") go(index - 1);
        if (event.key === "ArrowRight") go(index + 1);
      });
      window.addEventListener("resize", render, { passive: true });
      render();
      start();
    });
  };

  const initProducts = () => {
    $$("[data-lewa-product]").forEach((product) => {
      const media = $$("[data-lewa-product-media]", product);
      const thumbnails = $$("[data-lewa-product-thumbnail]", product);
      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", () => {
          const id = thumbnail.dataset.lewaProductThumbnail;
          media.forEach((item) => {
            const active = item.dataset.lewaProductMedia === id;
            item.hidden = !active;
            item.classList.toggle("is-active", active);
          });
          thumbnails.forEach((item) => {
            const active = item === thumbnail;
            item.classList.toggle("is-active", active);
            item.setAttribute("aria-pressed", String(active));
          });
        });
      });

      const variantsNode = $("[data-lewa-product-variants]", product);
      const variantInput = $("[data-lewa-variant-id]", product);
      const price = $("[data-lewa-product-price]", product);
      const add = $("[data-lewa-add-to-cart]", product);
      if (!variantsNode || !variantInput) return;
      let variants = [];
      try { variants = JSON.parse(variantsNode.textContent); } catch { return; }
      const updateVariant = () => {
        const selected = $$("[data-lewa-option-position]:checked", product)
          .sort((a, b) => Number(a.dataset.lewaOptionPosition) - Number(b.dataset.lewaOptionPosition))
          .map((input) => input.value);
        const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
        if (!variant) return;
        variantInput.value = variant.id;
        if (price) price.textContent = formatMoney(variant.price, window.Shopify?.currency?.active || "USD");
        if (add) {
          add.disabled = !variant.available;
          add.textContent = variant.available ? (add.dataset.availableLabel || "Add to bag") : (add.dataset.soldOutLabel || "Sold out");
        }
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      };
      $$("[data-lewa-option-position]", product).forEach((input) => input.addEventListener("change", updateVariant));
      if (variantInput.matches("select")) {
        variantInput.addEventListener("change", () => {
          const variant = variants.find((item) => String(item.id) === variantInput.value);
          if (!variant) return;
          if (price) price.textContent = formatMoney(variant.price, window.Shopify?.currency?.active || "USD");
          if (add) {
            add.disabled = !variant.available;
            add.textContent = variant.available ? "Add to bag" : "Sold out";
          }
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variant.id);
          window.history.replaceState({}, "", url);
        });
      }
    });
  };

  const escapeHtml = (value = "") => {
    const node = document.createElement("div");
    node.textContent = value;
    return node.innerHTML;
  };

  const formatMoney = (cents, currency = "USD") => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const cartRequest = async (path, options = {}) => {
    const response = await fetch(endpoint(path), {
      ...options,
      headers: { Accept: "application/json", ...(options.headers || {}) },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.description || body.message || "Cart update failed.");
    return body;
  };

  const initCart = () => {
    const drawer = $("[data-lewa-cart-drawer], .lewa-cart-drawer");
    const backdrop = $("[data-lewa-cart-backdrop], .lewa-backdrop, .lewa-cart-drawer__backdrop");
    if (!drawer || !backdrop) return;
    const openers = $$("[data-lewa-cart-open]");
    const error = $("[data-lewa-cart-error], .lewa-cart-error", drawer);
    const form = $("[data-lewa-cart-form]", drawer);
    const content = $("[data-lewa-cart-content]", drawer);

    const setError = (message = "") => {
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    };
    const open = () => {
      document.dispatchEvent(new CustomEvent("lewa:close-overlays"));
      state.lastFocus = document.activeElement;
      state.cartOpen = true;
      drawer.classList.add("lewa-is-open");
      backdrop.classList.add("lewa-is-open");
      drawer.setAttribute("aria-hidden", "false");
      setExpanded(openers, true);
      setScrollLock();
      focusFirst(drawer);
    };
    const close = ({ restoreFocus = true } = {}) => {
      state.cartOpen = false;
      drawer.classList.remove("lewa-is-open");
      backdrop.classList.remove("lewa-is-open");
      drawer.setAttribute("aria-hidden", "true");
      setExpanded(openers, false);
      setScrollLock();
      if (restoreFocus) state.lastFocus?.focus?.();
    };
    const lineTemplate = (item, currency) => {
      const image = item.image
        ? `<a href="${escapeHtml(item.url)}"><img class="lewa-cart-line__image" src="${escapeHtml(item.image)}&width=180" alt="${escapeHtml(item.product_title)}" width="90" height="113"></a>`
        : "";
      return `<li class="lewa-cart-line" data-lewa-cart-line data-line-key="${escapeHtml(item.key)}">
        ${image}<div><a class="lewa-cart-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
        ${item.variant_title ? `<p class="lewa-cart-line__variant">${escapeHtml(item.variant_title)}</p>` : ""}
        <div class="lewa-cart-line__controls">
          <button class="lewa-cart-line__change" type="button" data-lewa-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="Decrease quantity">-</button>
          <span aria-live="polite">${item.quantity}</span>
          <button class="lewa-cart-line__change" type="button" data-lewa-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          <button class="lewa-cart-line__remove lewa-link" type="button" data-lewa-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="0">Remove</button>
        </div></div><span class="lewa-price">${formatMoney(item.final_line_price, currency)}</span></li>`;
    };
    const render = (cart) => {
      $$("[data-lewa-cart-count], .lewa-cart-count").forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
        count.classList.toggle("lewa-header__cart-count--empty", cart.item_count === 0);
      });
      $$("[data-lewa-cart-subtotal], .lewa-cart-drawer__subtotal strong").forEach((subtotal) => {
        subtotal.textContent = formatMoney(cart.total_price, cart.currency);
      });
      if (content) {
        content.innerHTML = cart.item_count
          ? `<ul class="lewa-cart-lines" role="list">${cart.items.map((item) => lineTemplate(item, cart.currency)).join("")}</ul>`
          : `<div class="lewa-cart-drawer__empty"><p>Your bag is empty.</p><a class="lewa-cart-drawer__browse" href="${escapeHtml(window.LewaTheme?.allProductsUrl || endpoint("collections/all"))}">Browse bracelets</a></div>`;
      }
      let footer = $(".lewa-cart-drawer__footer", drawer);
      if (cart.item_count && !footer && form) {
        footer = document.createElement("div");
        footer.className = "lewa-cart-drawer__footer";
        form.append(footer);
      }
      if (footer) {
        footer.hidden = cart.item_count === 0;
        footer.innerHTML = `<div class="lewa-cart-drawer__subtotal"><span>Subtotal</span><strong>${formatMoney(cart.total_price, cart.currency)}</strong></div>
          <p class="lewa-cart-drawer__note">Shipping and taxes calculated at checkout.</p>
          <button class="lewa-cart-drawer__checkout" type="submit" name="checkout">Checkout</button>
          <a class="lewa-cart-drawer__view-cart" href="${endpoint("cart")}">View bag</a>`;
      }
      document.dispatchEvent(new CustomEvent("lewa:cart-updated", { detail: cart }));
    };
    const refresh = async () => render(await cartRequest("cart.js"));
    const change = async (key, quantity) => {
      setError();
      drawer.classList.add("lewa-is-loading");
      try {
        render(await cartRequest("cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: Number(quantity) }),
        }));
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        drawer.classList.remove("lewa-is-loading");
      }
    };

    openers.forEach((button) => button.addEventListener("click", open));
    $$("[data-lewa-cart-close]", drawer).forEach((button) => button.addEventListener("click", () => close()));
    backdrop.addEventListener("click", () => close());
    drawer.addEventListener("click", (event) => {
      const control = event.target.closest("[data-lewa-cart-change]");
      if (control) change(control.dataset.lineKey, control.dataset.quantity);
      const quantityControl = event.target.closest("[data-lewa-quantity-change]");
      if (quantityControl) {
        const line = quantityControl.closest("[data-lewa-cart-line]");
        const input = $("[data-lewa-quantity-input]", line);
        if (line && input) change(line.dataset.lewaCartLine, Number(input.value) + Number(quantityControl.dataset.lewaQuantityChange));
      }
      const remove = event.target.closest("[data-lewa-cart-remove]");
      if (remove) {
        event.preventDefault();
        const line = remove.closest("[data-lewa-cart-line]");
        if (line) change(line.dataset.lewaCartLine, 0);
      }
      if (event.target.closest("[data-lewa-cart-clear]")) changeAllClear();
    });
    const changeAllClear = async () => {
      setError();
      try {
        render(await cartRequest("cart/clear.js", { method: "POST" }));
      } catch (requestError) {
        setError(requestError.message);
      }
    };
    $$("form[data-lewa-add-to-cart], form[data-lewa-product-form], form[action*='/cart/add']").forEach((form) => {
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const submit = $("[type='submit']", form);
        submit?.classList.add("lewa-is-loading");
        if (submit) submit.disabled = true;
        setError();
        try {
          await cartRequest("cart/add.js", { method: "POST", body: new FormData(form) });
          await refresh();
          open();
        } catch (requestError) {
          setError(requestError.message);
          open();
        } finally {
          submit?.classList.remove("lewa-is-loading");
          if (submit) submit.disabled = false;
        }
      });
    });
    refresh().catch(() => {});
    document.addEventListener("lewa:close-overlays", () => {
      if (state.cartOpen) close({ restoreFocus: false });
    });
  };

  const initEscape = () => {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") document.dispatchEvent(new CustomEvent("lewa:close-overlays"));
    });
  };

  const init = () => {
    initHeader();
    initAnnouncement();
    initMobileMenu();
    initCarousel();
    initProducts();
    initCart();
    initEscape();
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
